import { useMemo } from 'react';
import { Box, Text } from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';

import { SlideKind } from '@duke-hq/svc-api-db';
import { EditorHTML } from '@duke-hq/ui/components/DukeTiptapEditor/EditorHTML';

import { FileUploadPromptAnswer } from './FileUploadPromptAnswer';
import { FormAnswer } from './FormAnswer';
import { SingleChoiceAnswer, SingleChoiceAnswerCard } from './SingleChoiceAnswer';
import type { FormattedSlide, Question } from './types';

type SlideQuestionProps = {
  slide: FormattedSlide;
  currentQuestion: Partial<Question>;
  setSelectedAnswerId: (answerId: string) => void;
  answerInput?: string;
  selectedAnswerId?: string;
  isReadOnly?: boolean;
  assessmentMode?: boolean | undefined;
  downloadFile: (fileId: string) => Promise<void>;
  getFileUrl?: (fileId: string) => Promise<string>;
  assessmentSubmissionId?: number;
  assessedAt?: Date | string | null;
};

export function SlideQuestion({
  slide,
  currentQuestion,
  setSelectedAnswerId,
  answerInput,
  selectedAnswerId,
  isReadOnly,
  assessmentMode = false,
  downloadFile,
  getFileUrl,
  assessmentSubmissionId,
  assessedAt,
}: SlideQuestionProps) {
  // Memoize the correctness check for the QUESTION_GROUP slide type
  const isQuestionGroupAnswerCorrect = useMemo(() => {
    if (slide.kind === SlideKind.QUESTION_GROUP && currentQuestion) {
      const selectedOption = currentQuestion.options?.find(
        (option) => option.id === selectedAnswerId,
      );
      return Boolean(selectedOption?.isCorrect);
    }
    return false;
  }, [slide.kind, selectedAnswerId, currentQuestion]);

  if (slide.kind === SlideKind.OPEN_QUESTION) {
    return <OpenQuestionAnswer answerInput={answerInput} />;
  } else if (slide.kind === SlideKind.QUESTION_GROUP && currentQuestion) {
    return (
      <div>
        {currentQuestion.questionContent ? (
          <EditorHTML content={currentQuestion.questionContent} />
        ) : null}
        <SingleChoiceAnswer value={selectedAnswerId} spacing="3" onChange={setSelectedAnswerId}>
          {currentQuestion.options?.map((option, i) => {
            return (
              <SingleChoiceAnswerCard
                key={option.id}
                value={option.id}
                isCorrect={assessmentMode ? isQuestionGroupAnswerCorrect : undefined}
                selectedOptions={selectedAnswerId ? [selectedAnswerId] : []}
                readOnly={isReadOnly}
                index={i}
              >
                <Text color="fg.emphasis" textStyle="sm">
                  {option.answer}
                </Text>
              </SingleChoiceAnswerCard>
            );
          })}
        </SingleChoiceAnswer>
      </div>
    );
  } else if (slide.kind === SlideKind.FILE_UPLOAD_PROMPT && currentQuestion) {
    return (
      <FileUploadPromptAnswer
        slide={slide}
        answerInput={answerInput}
        downloadFile={downloadFile}
        getFileUrl={getFileUrl}
      />
    );
  } else if (slide.kind === SlideKind.FORM && assessmentSubmissionId !== undefined) {
    return (
      <FormAnswer
        slide={slide}
        assessmentSubmissionId={assessmentSubmissionId}
        assessedAt={assessedAt}
      />
    );
  }
  return null;
}

function OpenQuestionAnswer({ answerInput }: { answerInput?: string }) {
  return (
    <Box
      mt={4}
      bg="bg.subtle"
      borderWidth="1px"
      borderColor="border.default"
      borderRadius="md"
      p={5}
    >
      <Text
        fontSize="xs"
        fontWeight="medium"
        letterSpacing="wider"
        textTransform="uppercase"
        color="fg.muted"
        mb={3}
      >
        <Trans>Student answer</Trans>
      </Text>

      {answerInput != null && answerInput.trim() !== '' ? (
        <Text fontSize="md" lineHeight="tall" whiteSpace="pre-wrap">
          {answerInput}
        </Text>
      ) : (
        <Text fontSize="sm" color="fg.muted" fontStyle="italic">
          <Trans>No answer provided</Trans>
        </Text>
      )}
    </Box>
  );
}
